import { getDirectoryBootstrap } from "@/transport/directory-bootstrap";
import type { ServerConnection } from "@/context/server";
import { getRuntimeBootstrap } from "@/transport/runtime-bootstrap";
import type {
  Agent,
  MuxClient,
  Project,
  Session,
} from "@mux/sdk/v2/client";
import { retry } from "@/utils/retry";
import { type SetStoreFunction, type Store } from "solid-js/store";
import { showToast } from "@mux/ui/toast";
import type { State, VcsCache, GlobalStore } from "./types";
import { formatServerError, isAbortError } from "@/utils/server-errors";
import { scheduleAfterInteractive } from "@/utils/interactive-schedule";
import {
  joinWorkspace,
  splitWorkspace,
  workspaceMatches,
  workspaceTitle,
} from "@/utils/workspace";

export type GlobalBootstrapResult = {
  criticalComplete: boolean;
  background: Promise<boolean>;
};

export async function bootstrapGlobal(input: {
  globalSDK: MuxClient;
  connection: ServerConnection.HttpBase;
  loadRuntimeBootstrap?: typeof getRuntimeBootstrap;
  requestFailedTitle: string;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  formatMoreCount: (count: number) => string;
  setGlobalStore: SetStoreFunction<GlobalStore>;
  setProjects: (projects: Project[]) => void;
  /** Installs each workspace's Oracle chat the bootstrap carries. */
  seedOracles: (oracles: Session[]) => void;
  /** Loads every open question and permission ask on the machine. */
  refreshRequests: () => Promise<unknown>;
}): Promise<GlobalBootstrapResult> {
  const bootstrap = await retry(() => (input.loadRuntimeBootstrap ?? getRuntimeBootstrap)(input.connection))
    .then((data) => ({ data }))
    .catch((error) => ({ error }));
  if ("error" in bootstrap && isAbortError(bootstrap.error)) {
    return { criticalComplete: false, background: Promise.resolve(false) };
  }
  if (!("data" in bootstrap) || !bootstrap.data.healthy) {
    input.setGlobalStore("ready", true);
    return { criticalComplete: false, background: Promise.resolve(false) };
  }
  input.setGlobalStore("path", bootstrap.data.path);
  input.setProjects(bootstrap.data.projects
    .filter((project) => !!project?.id)
    .filter((project) => !!project.worktree && !project.worktree.includes("mux-test"))
    .slice()
    .sort((a, b) => (b.time?.created ?? 0) - (a.time?.created ?? 0)));
  input.setGlobalStore("config", bootstrap.data.config);
  input.setGlobalStore("machineWorkspace", bootstrap.data.machineWorkspace);
  input.seedOracles(bootstrap.data.oracles);
  input.setGlobalStore("ready", true);

  // Requests reach every page as summary events; this covers what came before
  // the stream, in every workspace, whether or not it is open here.
  const requests = input.refreshRequests().then(() => true, (error) => {
    if (!isAbortError(error)) console.warn("Failed to load open questions and permission asks", error);
    return false;
  });
  return { criticalComplete: true, background: requests };
}

export function parseAgentList(value: unknown): Agent[] {
  if (!Array.isArray(value)) throw new TypeError("Invalid agent list response")
  return value as Agent[]
}

export type DirectoryBootstrapResult = {
  criticalComplete: boolean;
  metadata: Promise<boolean>;
};

const workspaceMetadataRetry = {
  attempts: 6,
  delay: 500,
  factor: 2,
  maxDelay: 15_000,
};

export function directoryBootstrapStatus(
  previous: State["status"],
  global: Pick<GlobalStore, "session" | "project">,
  directory: string,
): State["status"] {
  if (previous === "complete") return previous;
  // A workspace this device already knows renders while its snapshot loads.
  const known = global.project.some((project) => project.id === directory) ||
    global.session.some((session) => workspaceMatches(session, directory));
  return known ? "partial" : "loading";
}

export async function bootstrapDirectory(input: {
  directory: string;
  signal: AbortSignal;
  connection: ServerConnection.HttpBase;
  loadDirectoryBootstrap?: typeof getDirectoryBootstrap;
  sdk: MuxClient;
  store: Store<State>;
  setStore: SetStoreFunction<State>;
  vcsCache: VcsCache;
  loadSessions: (directory: string) => Promise<void> | void;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  globalStore: Store<GlobalStore>;
  setGlobalStore: SetStoreFunction<GlobalStore>;
  refreshSessionStatuses: () => Promise<void>;
}) {
  if (input.signal.aborted) return { criticalComplete: false, metadata: Promise.resolve(false) };
  const previousStatus = input.store.status;
  const initialStatus = directoryBootstrapStatus(
    previousStatus,
    input.globalStore,
    input.directory,
  );
  if (initialStatus !== previousStatus) input.setStore("status", initialStatus);

  try {
    const snapshotTask = retry(
      () => (input.loadDirectoryBootstrap ?? getDirectoryBootstrap)(input.connection, input.directory, input.signal),
      { ...workspaceMetadataRetry, signal: input.signal },
    );
    const [snapshot] = await Promise.all([
      snapshotTask,
      retry(async () => input.loadSessions(input.directory), { ...workspaceMetadataRetry, signal: input.signal }),
    ]);
    input.signal.throwIfAborted();
    input.setStore("project", snapshot.project.id);
    input.setStore("agent", parseAgentList(snapshot.agents));
    const source = splitWorkspace(input.directory);
    const target = splitWorkspace(snapshot.path.directory ?? input.directory);
    input.setStore("path", {
      ...snapshot.path,
      directory: joinWorkspace(target.root, source.id ?? target.id),
    });
    input.setStore("config", snapshot.config);
    input.setStore("command", snapshot.commands);
    input.setStore("lsp", snapshot.lsp);
    input.setStore("vcs", snapshot.vcs);
    if (snapshot.vcs?.branch) input.vcsCache.setStore("value", snapshot.vcs);
    input.setStore("status", "complete");

    const metadata = async (): Promise<PromiseSettledResult<void>[]> => {
      input.setStore("provider", input.globalStore.provider);
      return [];
    };

    const deferred = new Promise<DirectoryBootstrapResult>((resolve) => {
      const abort = () => {
        cancel();
        resolve({ criticalComplete: false, metadata: Promise.resolve(false) });
      };
      const cancel = scheduleAfterInteractive(() => {
        input.signal.removeEventListener("abort", abort);
        if (input.signal.aborted) return abort();
        const metadataTask = metadata().then((settled) => {
          const rejected = settled
            .filter((result): result is PromiseRejectedResult => result.status === "rejected")
            .map((result) => result.reason);
          const errors = rejected.filter((error) => !isAbortError(error));
          if (errors.length) console.warn("Failed to refresh deferred workspace metadata", errors[0]);
          return rejected.length === 0;
        });
        resolve({ criticalComplete: true, metadata: metadataTask });
      }, 100);
      input.signal.addEventListener("abort", abort, { once: true });
    });
    return deferred;
  } catch (err) {
    if (input.signal.aborted || isAbortError(err)) {
      return { criticalComplete: false, metadata: Promise.resolve(false) };
    }
    console.error("Failed to finish workspace bootstrap", err);
    const workspace = input.globalStore.project.find((project) => project.id === input.directory || project.id === input.store.project);
    const project = workspace?.name || workspaceTitle(workspace?.directory ?? input.directory, input.translate("command.category.workspace"));
    showToast({
      variant: "error",
      title: input.translate("toast.project.reloadFailed.title", { project }),
      description: formatServerError(err, input.translate),
    });
    input.setStore("status", "partial");
    return { criticalComplete: false, metadata: Promise.resolve(false) };
  }
}
